import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  ArrowLeft, 
  Download, 
  Send,
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Plus,
  Minus,
  Building2,
  Globe,
  Calendar,
  Hash
} from 'lucide-react';
import { Service, serviceApiService } from '../services/serviceApi';
import { Pack, packsApiService } from '../services/packsApi';
import { companyApiService, CompanyProfile } from '../services/companyApi';
import { useAuth } from '../components/AuthProvider';
import { purchaseRequestApiService, PurchaseRequestItem } from '../services/purchaseRequestApi';
import { promotionApiService, Promotion } from '../services/promotionApi';

interface DevisItem {
  service?: Service;
  pack?: Pack;
  type: 'service' | 'pack';
  quantity: number;
  pages?: number;
  description?: string;
}

interface ClientInfo {
  name: string;
  email: string;
  phone: string;
  company?: string;
  address?: string;
}

const FactureProformaPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<DevisItem[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: ''
  });
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [discount, setDiscount] = useState(0);
  const [tva, setTva] = useState(0); // TVA par défaut à 0
  const [userPromoCodes, setUserPromoCodes] = useState<Promotion[]>([]);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [availableServices, setAvailableServices] = useState<Service[]>([]);
  const [availablePacks, setAvailablePacks] = useState<Pack[]>([]);
  const [showAddService, setShowAddService] = useState(false);
  const [showPriceWarning, setShowPriceWarning] = useState(true);
  const [showPaymentNote, setShowPaymentNote] = useState(true);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState<string>('pending');
  const invoiceRef = useRef<HTMLDivElement>(null);

  // Fonction utilitaire pour sécuriser toFixed
  const safeToFixed = (value: any, decimals: number = 2) => {
    const num = Number(value || 0);
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };

  // Fonction pour transformer les items de la base de données en format utilisable
  const transformPurchaseRequestItems = async (dbItems: any[]): Promise<DevisItem[]> => {
    const transformedItems: DevisItem[] = [];

    for (const dbItem of dbItems) {
      console.log('🔄 Transformation item:', dbItem);
      
      if (dbItem.type === 'service' && dbItem.service) {
        try {
          // Récupérer les détails du service
          const service = await serviceApiService.getService(dbItem.service.toString());
          transformedItems.push({
            type: 'service' as const,
            service: {
              ...service,
              name: dbItem.service_name || service.name, // Utiliser le nom de la facture ou le nom par défaut
              price: parseFloat(dbItem.unit_price) || service.price // Utiliser le prix de la facture
            },
            pack: undefined,
            quantity: dbItem.quantity || 1,
            pages: dbItem.pages,
            description: dbItem.description || service.description
          });
          console.log('✅ Service transformé:', dbItem.service_name, 'Prix:', dbItem.unit_price);
        } catch (error) {
          console.error('Erreur lors du chargement du service:', dbItem.service, error);
          // Fallback: créer un item basique avec les données disponibles
          transformedItems.push({
            type: 'service' as const,
            service: {
              id: dbItem.service.toString(),
              name: dbItem.service_name || 'Service',
              price: parseFloat(dbItem.unit_price) || 0,
              description: dbItem.description || '',
              price_type: 'fixed' as const,
              unit: undefined,
              is_active: true,
              price_display: `${parseFloat(dbItem.unit_price) || 0} F CFA`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            pack: undefined,
            quantity: dbItem.quantity || 1,
            pages: dbItem.pages,
            description: dbItem.description || ''
          });
        }
      } else if (dbItem.type === 'pack' && dbItem.pack) {
        try {
          // Récupérer les détails du pack
          const pack = await packsApiService.getPack(dbItem.pack.toString());
          transformedItems.push({
            type: 'pack' as const,
            service: undefined,
            pack: {
              ...pack,
              name: dbItem.pack_name || pack.name, // Utiliser le nom de la facture
              total_price: parseFloat(dbItem.unit_price) || pack.total_price // Utiliser le prix de la facture
            },
            quantity: dbItem.quantity || 1,
            pages: undefined,
            description: dbItem.description || pack.description
          });
          console.log('✅ Pack transformé:', dbItem.pack_name, 'Prix:', dbItem.unit_price);
        } catch (error) {
          console.error('Erreur lors du chargement du pack:', dbItem.pack, error);
          // Fallback: créer un item basique avec les données disponibles
          transformedItems.push({
            type: 'pack' as const,
            service: undefined,
            pack: {
              id: dbItem.pack.toString(),
              name: dbItem.pack_name || 'Pack',
              total_price: parseFloat(dbItem.unit_price) || 0,
              description: dbItem.description || '',
              payment_facility: undefined,
              is_active: true,
              services: [],
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            quantity: dbItem.quantity || 1,
            pages: undefined,
            description: dbItem.description || ''
          });
        }
      }
    }

    return transformedItems;
  };

  // Fonction pour charger les données complètes d'une facture existante
  const loadPurchaseRequestData = useCallback(async (purchaseRequestId: number) => {
    try {
      setLoading(true);
      console.log('🔍 Chargement des données de la facture:', purchaseRequestId);
      
      // Utiliser l'API appropriée selon le type d'utilisateur
      let purchaseRequest;
      if (user?.isAdmin) {
        console.log('👤 Chargement avec API admin');
        purchaseRequest = await purchaseRequestApiService.getPurchaseRequestAdmin(purchaseRequestId.toString());
      } else {
        console.log('👤 Chargement avec API client (toutes les factures utilisateur)');
        // Pour les clients, récupérer toutes leurs factures et trouver celle demandée
        const userInvoices = await purchaseRequestApiService.getUserPurchaseRequests();
        console.log('🔍 Recherche facture ID:', purchaseRequestId, 'Type:', typeof purchaseRequestId);
        console.log('📋 Factures disponibles:', userInvoices.map(inv => ({ id: inv.id, type: typeof inv.id })));
        
        // Convertir purchaseRequestId en number pour la comparaison
        const targetId = typeof purchaseRequestId === 'string' ? parseInt(purchaseRequestId) : purchaseRequestId;
        purchaseRequest = userInvoices.find(invoice => invoice.id === targetId);
        
        if (!purchaseRequest) {
          throw new Error(`Facture ${purchaseRequestId} non trouvée dans vos factures. IDs disponibles: ${userInvoices.map(inv => inv.id).join(', ')}`);
        }
      }
      
      console.log('📋 Données de la facture chargées:', purchaseRequest);
      console.log('📦 Items reçus:', purchaseRequest.items);
      console.log('💰 Total reçu:', purchaseRequest.total_amount);
      console.log('🏷️ Remise reçue:', purchaseRequest.discount_percentage);

      // Charger et transformer les items
      if (purchaseRequest.items && purchaseRequest.items.length > 0) {
        console.log('🔄 Transformation des items...');
        const transformedItems = await transformPurchaseRequestItems(purchaseRequest.items);
        console.log('✅ Items transformés:', transformedItems);
        setItems(transformedItems);
      } else {
        console.log('❌ Aucun item trouvé dans purchaseRequest.items');
      }

      // Charger les informations de réduction
      if (purchaseRequest.discount_percentage) {
        setDiscount(Number(purchaseRequest.discount_percentage) || 0);
      }

      // Charger les informations client
      if (purchaseRequest.client_name || purchaseRequest.client_email || purchaseRequest.client_phone) {
        setClientInfo({
          name: purchaseRequest.client_name || '',
          email: purchaseRequest.client_email || '',
          phone: purchaseRequest.client_phone || '',
          company: purchaseRequest.client_company || '',
          address: purchaseRequest.client_address || ''
        });
      }

      // Stocker le statut de la facture
      setInvoiceStatus(purchaseRequest.status || 'pending');

      // Si c'est un admin qui consulte, activer le mode édition
      if (user?.isAdmin) {
        setEditMode(true);
      }

    } catch (error) {
      console.error('❌ Erreur lors du chargement de la facture:', error);
      alert('Erreur lors du chargement de la facture');
    } finally {
      setLoading(false);
    }
  }, [user?.isAdmin]);

  // Récupérer les données depuis la navigation et l'utilisateur connecté
  useEffect(() => {
    console.log('📥 Données reçues dans FactureProforma:', location.state);
    
    // Si on a un purchaseRequestId, charger les données complètes depuis l'API
    if (location.state?.purchaseRequestId) {
      console.log('🔄 Chargement des données depuis l\'API pour la facture:', location.state.purchaseRequestId);
      
      // Toujours charger depuis l'API pour avoir les données les plus récentes
      loadPurchaseRequestData(location.state.purchaseRequestId);
      
      // Mode visualisation pour les clients
      if (location.state?.viewMode && !user?.isAdmin) {
        setEditMode(false);
      }
    } else if (location.state?.selectedServices) {
      // Sinon, utiliser les services sélectionnés (nouveau devis)
      const services = location.state.selectedServices;
      setItems(services); // Utiliser directement les items avec quantités et pages
    }

    // Récupérer la réduction promo si elle existe
    if (location.state?.promoDiscount) {
      setDiscount(Number(location.state.promoDiscount) || 0);
      console.log('🎯 Réduction récupérée depuis DevisRequestPage:', location.state.promoDiscount);
    } else {
      console.log('❌ Aucune réduction trouvée dans location.state.promoDiscount:', location.state?.promoDiscount);
    }

    // Remplir automatiquement les infos client depuis l'utilisateur connecté
    if (user) {
      setClientInfo({
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        phone: user.phone || '',
        company: user.company || '',
        address: '' // L'adresse n'est pas dans le profil utilisateur de base
      });
    }

    loadCompanyProfile();
    if (user?.isAdmin) {
      loadAvailableServicesAndPacks();
    }
  }, [location.state, user, loadPurchaseRequestData]);

  const loadCompanyProfile = async () => {
    try {
      const profile = await companyApiService.getCompanyProfile();
      setCompanyProfile(profile);
      setTva(profile.tva_rate || 0);
    } catch (error) {
      console.error('Erreur lors du chargement du profil entreprise:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPromoCodes = useCallback(async () => {
    try {
      const promoCodes = await promotionApiService.getUserPromoCodes();
      setUserPromoCodes(promoCodes);
      
      // Appliquer automatiquement la meilleure réduction disponible seulement si aucune réduction n'est déjà définie
      // (pour ne pas écraser la réduction choisie dans DevisRequestPage)
      const hasPromoFromDevis = location.state?.promoDiscount && location.state.promoDiscount > 0;
      console.log('🔍 Vérification codes promo automatiques:', { 
        promoCodes: promoCodes.length, 
        currentDiscount: discount, 
        hasPromoFromDevis,
        locationPromoDiscount: location.state?.promoDiscount 
      });
      
      if (promoCodes.length > 0 && (discount || 0) === 0 && !hasPromoFromDevis) {
        // Filtrer seulement les promos en pourcentage
        const percentagePromos = promoCodes.filter(promo => promo.discount_type === 'percentage');
        
        if (percentagePromos.length > 0) {
          const bestPromo = percentagePromos.reduce((best, current) => 
            current.discount_value > best.discount_value ? current : best
          );
          setDiscount(Number(bestPromo.discount_value) || 0);
          console.log('🎯 Meilleure promo appliquée automatiquement:', bestPromo);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des codes promo:', error);
    }
  }, [discount, location.state?.promoDiscount]);

  const loadAvailableServicesAndPacks = async () => {
    try {
      const [services, packs] = await Promise.all([
        serviceApiService.getServices(),
        packsApiService.getPacks()
      ]);
      setAvailableServices(services);
      setAvailablePacks(packs);
    } catch (error) {
      console.error('Erreur lors du chargement des services et packs:', error);
    }
  };

  // Charger les codes promo de l'utilisateur seulement si pas de promo déjà choisie
  useEffect(() => {
    if (user && !location.state?.promoDiscount) {
      loadUserPromoCodes();
    }
  }, [user, location.state?.promoDiscount, loadUserPromoCodes]);

  // Calculs
  const calculateSubtotal = () => {
    return items.reduce((total, item) => {
      if (item.type === 'service' && item.service) {
        const basePrice = item.service.price || 0;
        // Si le service a des pages, le prix total = prix de base × nombre de pages
        const totalServicePrice = item.pages ? basePrice * item.pages : basePrice;
        return total + totalServicePrice * item.quantity;
      } else if (item.type === 'pack' && item.pack) {
        return total + item.pack.total_price * item.quantity;
      }
      return total;
    }, 0);
  };

  // Vérifier s'il y a des services avec prix "à partir de"
  const nonFixedPriceServices = items.filter(item => 
    item.type === 'service' && item.service?.price_type === 'from'
  );

  // Vérifier s'il y a des packs contenant des services avec prix "à partir de"
  const nonFixedPricePacks = items.filter(item => 
    item.type === 'pack' && 
    item.pack?.services && 
    item.pack.services.some(service => service.price_type === 'from')
  );

  const hasNonFixedPrices = nonFixedPriceServices.length > 0 || nonFixedPricePacks.length > 0;


  const subtotal = calculateSubtotal();
  const discountAmount = (subtotal * (discount || 0)) / 100;
  const subtotalAfterDiscount = subtotal - discountAmount;
  const tvaAmount = (subtotalAfterDiscount * (tva || 0)) / 100;
  const total = subtotalAfterDiscount + tvaAmount;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(price);
  };

  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `FP${year}${month}${day}-${random}`;
  };

  const [invoiceNumber] = useState(generateInvoiceNumber());

  // Fonction pour télécharger la facture en PDF
  const generatePDF = async () => {
    if (!invoiceRef.current) return;

    try {
      setDownloadingPDF(true);

      // Créer un canvas à partir de tout le contenu (devis + annexe)
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 1.5, // Qualité optimisée pour PDF
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        height: invoiceRef.current.scrollHeight,
        windowHeight: invoiceRef.current.scrollHeight
      });

      // Créer le PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Calculer les dimensions pour s'adapter à la page A4
      const imgWidth = 210; // Largeur A4 en mm
      const pageHeight = 297; // Hauteur A4 en mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calculer le nombre de pages nécessaires
      const totalPages = Math.ceil(imgHeight / pageHeight);
      
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) {
          pdf.addPage();
        }
        
        const yPosition = -(i * pageHeight);
        pdf.addImage(imgData, 'PNG', 0, yPosition, imgWidth, imgHeight);
      }

      // Télécharger le PDF
      const invoiceNumber = generateInvoiceNumber();
      pdf.save(`devis-proforma-${invoiceNumber}.pdf`);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Fonctions d'édition pour les admins
  const updateItemQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    const updatedItems = [...items];
    updatedItems[index].quantity = newQuantity;
    setItems(updatedItems);
  };

  const updateItemPages = (index: number, newPages: number) => {
    if (newPages < 1) return;
    const updatedItems = [...items];
    updatedItems[index].pages = newPages;
    setItems(updatedItems);
  };

  const updateServicePrice = (index: number, newPrice: number) => {
    // Permettre 0 (gratuit) mais pas de prix négatifs
    const validPrice = Math.max(0, newPrice);
    const updatedItems = [...items];
    if (updatedItems[index].service) {
      updatedItems[index].service!.price = validPrice;
    }
    setItems(updatedItems);
  };

  const updatePackPrice = (index: number, newPrice: number) => {
    // Permettre 0 (gratuit) mais pas de prix négatifs
    const validPrice = Math.max(0, newPrice);
    const updatedItems = [...items];
    if (updatedItems[index].pack) {
      updatedItems[index].pack!.total_price = validPrice;
    }
    setItems(updatedItems);
  };

  const removeItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const updateClientInfo = (field: keyof typeof clientInfo, value: string) => {
    setClientInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addService = (service: Service) => {
    const newItem: DevisItem = {
      service,
      type: 'service',
      quantity: 1,
      pages: service.price_type === 'per_unit' ? 1 : undefined
    };
    setItems(prev => [...prev, newItem]);
    setShowAddService(false);
  };

  const addPack = (pack: Pack) => {
    const newItem: DevisItem = {
      pack,
      type: 'pack',
      quantity: 1
    };
    setItems(prev => [...prev, newItem]);
    setShowAddService(false);
  };

  const addDiscount = (percentage: number) => {
    setDiscount(percentage);
  };

  // Fonction pour renvoyer la facture révisée (admin uniquement)
  const sendRevisedInvoice = async () => {
    if (!user?.isAdmin) {
      alert('Seuls les administrateurs peuvent renvoyer des factures révisées');
      return;
    }

    try {
      setSendingInvoice(true);

      // Préparer les données de la facture révisée
      const status: 'confirmed' | 'revision' = 'revision'; // Statut "revisitée"

      // Préparer les items de la demande avec les modifications
      const requestItems: PurchaseRequestItem[] = items.map(item => {
        if (item.type === 'service' && item.service) {
          const basePrice = item.service.price || 0;
          const unitPrice = item.pages ? basePrice : basePrice;
          const totalPrice = item.pages ? basePrice * item.pages * item.quantity : basePrice * item.quantity;

          return {
            service_id: item.service.id,
            service_name: item.service.name,
            pack_id: undefined,
            pack_name: undefined,
            type: 'service',
            quantity: item.quantity,
            pages: item.pages,
            unit_price: unitPrice,
            total_price: totalPrice,
            description: item.service.description || ''
          };
        } else if (item.type === 'pack' && item.pack) {
          return {
            service_id: undefined,
            service_name: undefined,
            pack_id: item.pack.id,
            pack_name: item.pack.name,
            type: 'pack',
            quantity: item.quantity,
            pages: undefined,
            unit_price: item.pack.total_price,
            total_price: item.pack.total_price * item.quantity,
            description: item.pack.description || ''
          };
        } else {
          return {
            service_id: undefined,
            service_name: undefined,
            pack_id: undefined,
            pack_name: undefined,
            type: 'service',
            quantity: item.quantity,
            pages: undefined,
            unit_price: 0,
            total_price: 0,
            description: ''
          };
        }
      });

      const requestData = {
        status,
        subtotal,
        discount_percentage: discount || 0,
        discount_amount: discountAmount,
        tva_percentage: tva,
        tva_amount: tvaAmount,
        total,
        total_amount: total, // Propriété requise
        has_variable_pricing: hasNonFixedPrices, // Propriété requise
        client_name: clientInfo.name,
        client_email: clientInfo.email,
        client_phone: clientInfo.phone,
        client_company: clientInfo.company,
        client_address: clientInfo.address,
        items: requestItems
      };

      console.log('📤 Envoi de la facture révisée:', requestData);
      console.log('🔍 Statut envoyé:', requestData.status);
      console.log('🔄 WORKFLOW ADMIN: Modification facture ID', location.state.purchaseRequestId, 'vers statut "revision"');

      // Vérifier si on modifie une demande existante ou on en crée une nouvelle
      if (location.state?.purchaseRequestId) {
        // Mettre à jour la demande existante avec toutes les données ET le statut "revision"
        console.log('🔄 Mise à jour complète de la demande existante:', location.state.purchaseRequestId);
        console.log('📋 Données à envoyer (avec statut revision):', JSON.stringify(requestData, null, 2));
        
        // Un seul appel API qui met tout à jour : données + statut
        const updateResponse = await purchaseRequestApiService.updatePurchaseRequest(location.state.purchaseRequestId, requestData);
        console.log('✅ Facture complètement mise à jour:', updateResponse);
        
        // TEST: Vérifier immédiatement si la mise à jour a fonctionné
        console.log('🧪 TEST: Vérification immédiate de la mise à jour...');
        setTimeout(async () => {
          try {
            const verificationResponse = await purchaseRequestApiService.getPurchaseRequest(location.state.purchaseRequestId);
            console.log('🧪 TEST: Facture après mise à jour:', verificationResponse);
            console.log('🧪 TEST: Statut actuel:', verificationResponse.status);
            console.log('🧪 TEST: Montant actuel:', verificationResponse.total_amount);
            console.log('🧪 TEST: Updated_at:', verificationResponse.updated_at);
          } catch (error) {
            console.error('🧪 TEST: Erreur lors de la vérification:', error);
          }
        }, 1000);
        
        // Marquer qu'une facture a été mise à jour pour forcer le rechargement côté client
        localStorage.setItem('invoiceUpdated', Date.now().toString());
        localStorage.setItem('updatedInvoiceId', location.state.purchaseRequestId);
        
        alert('✅ Facture révisée et renvoyée avec succès !');
      } else {
        // Créer une nouvelle demande avec statut "revision"
        console.log('➕ Création d\'une nouvelle demande avec statut "revision"');
        const newRequest = await purchaseRequestApiService.createPurchaseRequest(requestData);
        console.log('✅ Nouvelle facture créée:', newRequest);
        alert('✅ Facture révisée créée et envoyée avec succès !');
      }

      // Rediriger vers la page des demandes clients
      navigate('/demandesclients');

    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi de la facture révisée:', error);
      alert('Une erreur est survenue lors de l\'envoi de la facture révisée. Veuillez réessayer.');
    } finally {
      setSendingInvoice(false);
    }
  };

  // Fonction pour envoyer la demande d'achat
  const sendPurchaseRequest = async () => {
    if (!user) {
      alert('Vous devez être connecté pour envoyer une demande d\'achat');
      return;
    }

    try {
      setSendingRequest(true);

      // Toutes les demandes sont mises en statut "pending" (en attente)
      const status: 'pending' = 'pending';

      // Préparer les items de la demande
      const requestItems: PurchaseRequestItem[] = items.map(item => {
        if (item.type === 'service' && item.service) {
          const basePrice = item.service.price || 0;
          const unitPrice = item.pages ? basePrice : basePrice;
          const totalPrice = item.pages ? basePrice * item.pages * item.quantity : basePrice * item.quantity;
          
          return {
            service_id: item.service.id,
            service_name: item.service.name,
            pack_id: undefined,
            pack_name: undefined,
            type: 'service',
            quantity: item.quantity,
            pages: item.pages,
            unit_price: unitPrice,
            total_price: totalPrice,
            description: item.service.description || ''
          };
        } else if (item.type === 'pack' && item.pack) {
          return {
            service_id: undefined,
            service_name: undefined,
            pack_id: item.pack.id,
            pack_name: item.pack.name,
            type: 'pack',
            quantity: item.quantity,
            pages: undefined,
            unit_price: item.pack.total_price,
            total_price: item.pack.total_price * item.quantity,
            description: item.pack.description || ''
          };
        } else {
          // Fallback pour les items invalides
          return {
            service_id: undefined,
            service_name: undefined,
            pack_id: undefined,
            pack_name: undefined,
            type: 'service',
            quantity: item.quantity,
            pages: undefined,
            unit_price: 0,
            total_price: 0,
            description: ''
          };
        } 
      });

      // Préparer les données de la demande (sans user_id car défini automatiquement par le backend)
      const requestData = {
        status,
        subtotal,
        discount_percentage: discount || 0,
        discount_amount: discountAmount,
        tva_percentage: tva || 0,
        tva_amount: tvaAmount,
        total_amount: total,
        promo_code: location.state?.promoCode || undefined,
        client_name: clientInfo.name || `${user.firstName} ${user.lastName}`,
        client_email: clientInfo.email || user.email,
        client_phone: clientInfo.phone || user.phone,
        client_company: clientInfo.company || user.company,
        client_address: clientInfo.address,
        has_variable_pricing: hasNonFixedPrices,
        items: requestItems
      };

      console.log('📤 Envoi de la demande d\'achat:', requestData);
      console.log('📋 Items détaillés:', requestItems);
      console.log('🔄 WORKFLOW CLIENT: Création nouvelle facture avec statut "pending"');

      const response = await purchaseRequestApiService.createPurchaseRequest(requestData);
      
      console.log('✅ Demande d\'achat créée:', response);

      // Si un code promo est utilisé, le passer en statut pending
      const promoCodeUsed = location.state?.promoCode;
      if (promoCodeUsed) {
        try {
          console.log('⏳ Passage du code promo en pending:', promoCodeUsed);
          await promotionApiService.setPromoCodePending(promoCodeUsed);
          console.log('✅ Code promo passé en pending');
        } catch (promoError) {
          console.error('⚠️ Erreur lors du passage en pending:', promoError);
          // Continuer malgré l'erreur de promo
        }
      }

      // Afficher un message de succès
      alert('✅ Demande d\'achat envoyée avec succès ! Votre demande est en cours de traitement par notre équipe.');

      // Rediriger vers la page des factures client après envoi
      navigate('/factures');

    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi de la demande:', error);
      alert('Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer.');
    } finally {
      setSendingRequest(false);
    }
  };

  // Fonction pour confirmer une facture révisée (client uniquement)
  const confirmInvoice = async () => {
    if (!user || user.isAdmin) {
      alert('Seuls les clients peuvent confirmer une facture');
      return;
    }

    if (!location.state?.purchaseRequestId) {
      alert('ID de facture manquant');
      return;
    }

    try {
      setSendingRequest(true);
      
      console.log('✅ Confirmation de la facture ID:', location.state.purchaseRequestId);
      
      // Mettre à jour uniquement le statut vers "confirmed"
      await purchaseRequestApiService.updatePurchaseRequestStatus(
        location.state.purchaseRequestId, 
        'confirmed'
      );
      
      alert('✅ Facture confirmée avec succès !');
      navigate('/factures'); // Retour à la liste des factures client
      
    } catch (error) {
      console.error('❌ Erreur lors de la confirmation:', error);
      alert('Une erreur est survenue lors de la confirmation. Veuillez réessayer.');
    } finally {
      setSendingRequest(false);
    }
  };

  // Fonction pour annuler une facture révisée (client uniquement)
  const cancelInvoice = async () => {
    if (!user || user.isAdmin) {
      alert('Seuls les clients peuvent annuler une facture');
      return;
    }

    if (!location.state?.purchaseRequestId) {
      alert('ID de facture manquant');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette facture ?')) {
      return;
    }

    try {
      setSendingRequest(true);
      
      console.log('❌ Annulation de la facture ID:', location.state.purchaseRequestId);
      
      // Mettre à jour uniquement le statut vers "cancelled"
      await purchaseRequestApiService.updatePurchaseRequestStatus(
        location.state.purchaseRequestId, 
        'cancelled'
      );
      
      alert('❌ Facture annulée avec succès !');
      navigate('/factures'); // Retour à la liste des factures client
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'annulation:', error);
      alert('Une erreur est survenue lors de l\'annulation. Veuillez réessayer.');
    } finally {
      setSendingRequest(false);
    }
  };

  // Fonction pour réactiver une facture confirmée (la remettre en révision)
  const reactivateInvoice = async () => {
    if (!user || user.isAdmin) {
      alert('Seuls les clients peuvent réactiver une facture');
      return;
    }

    if (!location.state?.purchaseRequestId) {
      alert('ID de facture manquant');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir réactiver cette facture ? Elle repassera en statut "révision".')) {
      return;
    }

    try {
      setSendingRequest(true);
      
      console.log('🔄 Réactivation de la facture ID:', location.state.purchaseRequestId);
      
      // Mettre à jour le statut vers "revision"
      await purchaseRequestApiService.updatePurchaseRequestStatus(
        location.state.purchaseRequestId, 
        'revision'
      );
      
      alert('🔄 Facture réactivée avec succès !');
      // Recharger les données pour mettre à jour l'affichage
      if (location.state.purchaseRequestId) {
        loadPurchaseRequestData(location.state.purchaseRequestId);
      }
      
    } catch (error) {
      console.error('❌ Erreur lors de la réactivation:', error);
      alert('Une erreur est survenue lors de la réactivation. Veuillez réessayer.');
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>
            
            <div className="flex items-center space-x-4">
              {user?.isAdmin && (
                <button 
                  onClick={() => setEditMode(!editMode)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    editMode 
                      ? 'bg-orange-600 text-white hover:bg-orange-700' 
                      : 'text-orange-600 border border-orange-600 hover:bg-orange-50'
                  }`}
                >
                  <Edit className="w-4 h-4" />
                  <span>{editMode ? 'Terminer édition' : 'Modifier facture'}</span>
                </button>
              )}
              <button 
                onClick={generatePDF}
                disabled={downloadingPDF}
                className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className={`w-4 h-4 ${downloadingPDF ? 'animate-spin' : ''}`} />
                <span>{downloadingPDF ? 'Génération...' : 'Télécharger PDF'}</span>
              </button>
              {user?.isAdmin ? (
                <button 
                  onClick={sendRevisedInvoice}
                  disabled={sendingInvoice}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className={`w-4 h-4 ${sendingInvoice ? 'animate-spin' : ''}`} />
                  <span>{sendingInvoice ? 'Envoi...' : 'Renvoyer la facture'}</span>
                </button>
              ) : invoiceStatus === 'revision' ? (
                // Boutons pour client avec facture révisée
                <>
                  <button 
                    onClick={confirmInvoice}
                    disabled={sendingRequest}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className={`w-4 h-4 ${sendingRequest ? 'animate-spin' : ''}`} />
                    <span>{sendingRequest ? 'Confirmation...' : 'Confirmer'}</span>
                  </button>
                  <button 
                    onClick={cancelInvoice}
                    disabled={sendingRequest}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className={`w-4 h-4 ${sendingRequest ? 'animate-spin' : ''}`} />
                    <span>{sendingRequest ? 'Annulation...' : 'Annuler'}</span>
                  </button>
                </>
              ) : invoiceStatus === 'confirmed' ? (
                // Bouton pour facture confirmée - possibilité d'annuler
                <button 
                  onClick={cancelInvoice}
                  disabled={sendingRequest}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle className={`w-4 h-4 ${sendingRequest ? 'animate-spin' : ''}`} />
                  <span>{sendingRequest ? 'Annulation...' : 'Annuler la facture'}</span>
                </button>
              ) : invoiceStatus === 'cancelled' ? (
                // Bouton pour facture annulée - possibilité de confirmer
                <button 
                  onClick={confirmInvoice}
                  disabled={sendingRequest}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className={`w-4 h-4 ${sendingRequest ? 'animate-spin' : ''}`} />
                  <span>{sendingRequest ? 'Confirmation...' : 'Confirmer la facture'}</span>
                </button>
              ) : (
                // Bouton normal pour client avec nouvelle facture
                <button 
                  onClick={sendPurchaseRequest}
                  disabled={sendingRequest}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className={`w-4 h-4 ${sendingRequest ? 'animate-spin' : ''}`} />
                  <span>{sendingRequest ? 'Envoi...' : 'Envoyer la demande'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages d'information pour les statuts finalisés */}
      {!user?.isAdmin && invoiceStatus === 'confirmed' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg mb-6">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-green-800 font-semibold mb-2">
                  ✅ Facture confirmée avec succès !
                </h3>
                <p className="text-green-700 text-sm mb-2">
                  Vous avez déjà confirmé cette facture. Un expert IRIS SOFTECH vous contactera bientôt pour démarrer le travail.
                </p>
                <p className="text-green-700 text-sm">
                  Vous pourrez suivre l'évolution de votre projet dans la section{' '}
                  <button 
                    onClick={() => navigate('/projets')}
                    className="font-semibold underline hover:text-green-900 transition-colors"
                  >
                    /projets
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!user?.isAdmin && invoiceStatus === 'cancelled' && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg mb-6">
            <div className="flex items-start space-x-3">
              <XCircle className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-red-800 font-semibold mb-2">
                  ❌ Facture annulée
                </h3>
                <p className="text-red-700 text-sm mb-2">
                  Cette facture a été annulée. Vous pouvez la confirmer directement en cliquant sur le bouton "Confirmer la facture" ci-dessous.
                </p>
                <p className="text-red-700 text-sm">
                  Si vous préférez créer une nouvelle demande, retournez à la section{' '}
                  <button 
                    onClick={() => navigate('/devisrequest')}
                    className="font-semibold underline hover:text-red-900 transition-colors"
                  >
                    demande de devis
                  </button>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div ref={invoiceRef} className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl mx-auto main-invoice"
        >
          {/* En-tête de la facture */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8">
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-4">
                {(companyProfile?.logo_url || companyProfile?.logo) ? (
                  <img 
                    src={companyProfile.logo_url || companyProfile.logo} 
                    alt="Logo entreprise" 
                    className="w-16 h-16 rounded-lg bg-white p-2"
                    onLoad={() => console.log('✅ Logo chargé dans la facture:', companyProfile.logo_url || companyProfile.logo)}
                    onError={() => console.log('❌ Erreur logo dans la facture:', companyProfile.logo_url || companyProfile.logo)}
                  />
                ) : (
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-blue-600" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold">{companyProfile?.name || 'IRIS SOFTECH'}</h1>
                  {companyProfile?.slogan && (
                    <p className="text-blue-100 mt-1">{companyProfile.slogan}</p>
                  )}
                </div>
              </div>
              
              <div className="text-right">
                <h2 className="text-3xl font-bold mb-2">FACTURE PROFORMA</h2>
                <div className="space-y-1 text-blue-100">
                  <p className="flex items-center justify-end space-x-2">
                    <Hash className="w-4 h-4" />
                    <span>{invoiceNumber}</span>
                  </p>
                  <p className="flex items-center justify-end space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date().toLocaleDateString('fr-FR')}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Message d'avertissement pour les prix non-fixes */}
          {hasNonFixedPrices && showPriceWarning && invoiceStatus === 'pending' && (
            <div className="mx-8 mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-amber-800 font-semibold mb-2">
                    ⚠️ Devis provisoire - Révision nécessaire
                  </h4>
                  <p className="text-amber-700 text-sm mb-2">
                    Les éléments suivants ont des prix "à partir de" qui nécessitent une évaluation personnalisée :
                  </p>
                  <ul className="text-amber-700 text-sm space-y-1 mb-3">
                    {/* Services avec prix variables */}
                    {nonFixedPriceServices.map((item, index) => (
                      <li key={`service-${index}`} className="flex items-center space-x-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        <span className="font-medium">{item.service?.name}</span>
                        <span className="text-xs text-amber-600">
                          (À partir de {item.service?.price?.toLocaleString()} FCFA)
                        </span>
                      </li>
                    ))}
                    
                    {/* Packs contenant des services avec prix variables */}
                    {nonFixedPricePacks.map((item, index) => (
                      <li key={`pack-${index}`} className="flex items-center space-x-2">
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                        <span className="font-medium">{item.pack?.name} <span className="text-purple-600">(Pack)</span></span>
                        <span className="text-xs text-amber-600">
                          (Contient des services à prix variables)
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-amber-800 text-sm font-medium bg-amber-100 p-3 rounded-md">
                    📧 Ce devis sera révisé et confirmé par un expert IRIS SOFTECH.<br/>
                    La facture définitive vous sera renvoyée par <strong>email</strong> et <strong>WhatsApp</strong> dans les plus brefs délais.
                  </div>
                </div>
                {user?.isAdmin && (
                  <button
                    onClick={() => setShowPriceWarning(false)}
                    className="text-amber-400 hover:text-amber-600 transition-colors ml-2 mt-0.5"
                    title="Masquer ce message"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Informations entreprise et client */}
          <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Informations de l'entreprise */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                De
              </h3>
              <div className="space-y-2 text-gray-600">
                <p className="font-semibold text-gray-900">{companyProfile?.name || 'IRIS SOFTECH'}</p>
                {companyProfile?.email && (
                  <p className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>{companyProfile.email}</span>
                  </p>
                )}
                {companyProfile?.phone && (
                  <p className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>{companyProfile.phone}</span>
                  </p>
                )}
                {companyProfile?.website && (
                  <p className="flex items-center space-x-2">
                    <Globe className="w-4 h-4" />
                    <span>{companyProfile.website}</span>
                  </p>
                )}
                {companyProfile?.address && (
                  <p className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>{companyProfile.address}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Informations du client */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-blue-600" />
                À
              </h3>
              <div className="space-y-2 text-gray-600">
                {editMode && user?.isAdmin ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
                      <input
                        type="text"
                        value={clientInfo.name}
                        onChange={(e) => updateClientInfo('name', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="Nom du client"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Entreprise</label>
                      <input
                        type="text"
                        value={clientInfo.company}
                        onChange={(e) => updateClientInfo('company', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="Nom de l'entreprise"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={clientInfo.email}
                        onChange={(e) => updateClientInfo('email', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="email@exemple.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                      <input
                        type="tel"
                        value={clientInfo.phone}
                        onChange={(e) => updateClientInfo('phone', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="771742350"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Adresse</label>
                      <textarea
                        value={clientInfo.address}
                        onChange={(e) => updateClientInfo('address', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        placeholder="Adresse complète"
                        rows={2}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    {clientInfo.name && (
                      <p className="font-semibold text-gray-900">{clientInfo.name}</p>
                    )}
                    {clientInfo.company && (
                      <p className="text-sm text-gray-500">{clientInfo.company}</p>
                    )}
                    {clientInfo.email && (
                      <p className="flex items-center space-x-2">
                        <Mail className="w-4 h-4" />
                        <span>{clientInfo.email}</span>
                      </p>
                    )}
                    {clientInfo.phone && (
                      <p className="flex items-center space-x-2">
                        <Phone className="w-4 h-4" />
                        <span>{clientInfo.phone}</span>
                      </p>
                    )}
                    {clientInfo.address && (
                      <p className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4" />
                        <span>{clientInfo.address}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
              {!clientInfo.name && !clientInfo.email && (
                <p className="text-gray-400 italic">Informations client non disponibles</p>
              )}
            </div>
          </div>

          {/* Tableau des services */}
          <div className="px-8 pb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Détail des prestations
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Désignation</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Qté</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Prix unitaire</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item, index) => {
                    // Variables typées pour éviter les erreurs TypeScript
                    let displayQuantity: number;
                    let unitPrice: number;
                    let totalPrice: number;
                    let itemName: string;
                    
                    if (item.type === 'service' && item.service) {
                      const basePrice = item.service.price || 0;
                      itemName = item.service.name;
                      
                      if (item.pages) {
                        // Pour les services avec pages : quantité = nombre de pages, prix unitaire = prix par page
                        displayQuantity = item.pages;
                        unitPrice = basePrice;
                        totalPrice = basePrice * item.pages * item.quantity;
                      } else {
                        // Pour les services sans pages : quantité normale
                        displayQuantity = item.quantity;
                        unitPrice = basePrice;
                        totalPrice = basePrice * item.quantity;
                      }
                    } else if (item.type === 'pack' && item.pack) {
                      itemName = item.pack.name;
                      displayQuantity = item.quantity;
                      unitPrice = item.pack.total_price;
                      totalPrice = item.pack.total_price * item.quantity;
                    } else {
                      // Fallback pour les cas non gérés
                      itemName = 'Élément inconnu';
                      displayQuantity = item.quantity;
                      unitPrice = 0;
                      totalPrice = 0;
                    }

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <div>
                              <p className="font-medium text-gray-900">
                                {itemName}
                                {item.pages && ` (${item.pages} pages)`}
                                {item.type === 'pack' && <span className="text-purple-600 text-sm ml-2">(Pack)</span>}
                              </p>
                              {/* Indicateur pour services avec prix variables */}
                              {item.type === 'service' && item.service?.price_type === 'from' && (
                                <div className="flex items-center space-x-1 mt-1">
                                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                                  <span className="text-xs text-amber-600 font-medium">
                                    Prix variable
                                  </span>
                                </div>
                              )}
                              
                              {/* Indicateur pour packs contenant des services avec prix variables */}
                              {item.type === 'pack' && item.pack?.services?.some(service => service.price_type === 'from') && (
                                <div className="flex items-center space-x-1 mt-1">
                                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                                  <span className="text-xs text-amber-600 font-medium">
                                    Pack avec prix variables
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {editMode && user?.isAdmin ? (
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => updateItemQuantity(index, item.quantity - 1)}
                                className="w-6 h-6 flex items-center justify-center rounded bg-red-100 text-red-600 hover:bg-red-200"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)}
                                className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm"
                                min="1"
                              />
                              <button
                                onClick={() => updateItemQuantity(index, item.quantity + 1)}
                                className="w-6 h-6 flex items-center justify-center rounded bg-green-100 text-green-600 hover:bg-green-200"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                              {item.pages && (
                                <div className="ml-2 flex items-center space-x-1">
                                  <span className="text-xs text-gray-500">Pages:</span>
                                  <input
                                    type="number"
                                    value={item.pages}
                                    onChange={(e) => updateItemPages(index, parseInt(e.target.value) || 1)}
                                    className="w-12 text-center border border-gray-300 rounded px-1 py-1 text-xs"
                                    min="1"
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                              {displayQuantity}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right font-medium text-gray-900">
                          {editMode && user?.isAdmin ? (
                            <div className="flex items-center justify-end space-x-1">
                              <button
                                onClick={() => {
                                  const currentPrice = unitPrice;
                                  const step = currentPrice >= 1000 ? 1000 : currentPrice >= 100 ? 100 : 10;
                                  const newPrice = Math.max(0, currentPrice - step);
                                  if (item.type === 'service') {
                                    updateServicePrice(index, newPrice);
                                  } else if (item.type === 'pack') {
                                    updatePackPrice(index, newPrice);
                                  }
                                }}
                                className="w-6 h-6 flex items-center justify-center rounded bg-red-100 text-red-600 hover:bg-red-200 text-xs"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                value={unitPrice}
                                onChange={(e) => {
                                  const newPrice = Math.max(0, parseFloat(e.target.value) || 0);
                                  if (item.type === 'service') {
                                    updateServicePrice(index, newPrice);
                                  } else if (item.type === 'pack') {
                                    updatePackPrice(index, newPrice);
                                  }
                                }}
                                className="w-20 text-right border border-gray-300 rounded px-1 py-1 text-xs"
                                min="0"
                                step="0.01"
                              />
                              <button
                                onClick={() => {
                                  const currentPrice = unitPrice;
                                  const step = currentPrice >= 1000 ? 1000 : currentPrice >= 100 ? 100 : 10;
                                  const newPrice = currentPrice + step;
                                  if (item.type === 'service') {
                                    updateServicePrice(index, newPrice);
                                  } else if (item.type === 'pack') {
                                    updatePackPrice(index, newPrice);
                                  }
                                }}
                                className="w-6 h-6 flex items-center justify-center rounded bg-green-100 text-green-600 hover:bg-green-200 text-xs"
                              >
                                +
                              </button>
                              <button
                                onClick={() => {
                                  if (item.type === 'service') {
                                    updateServicePrice(index, 0);
                                  } else if (item.type === 'pack') {
                                    updatePackPrice(index, 0);
                                  }
                                }}
                                className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                title="Offert"
                              >
                                Offert
                              </button>
                            </div>
                          ) : (
                            <span className={unitPrice === 0 ? 'text-purple-600 font-semibold' : ''}>
                              {unitPrice === 0 ? 'OFFERT' : formatPrice(unitPrice)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right font-semibold text-gray-900">
                          <div className="flex items-center justify-end space-x-2">
                            {formatPrice(totalPrice)}
                            {editMode && user?.isAdmin && (
                              <button
                                onClick={() => removeItem(index)}
                                className="w-6 h-6 flex items-center justify-center rounded bg-red-100 text-red-600 hover:bg-red-200"
                                title="Supprimer cet item"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Bouton pour ajouter des services (mode édition) */}
            {editMode && user?.isAdmin && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => setShowAddService(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ajouter un service/pack</span>
                </button>
              </div>
            )}

            {/* Totaux */}
            <div className="mt-6 flex justify-end">
              <div className="w-full max-w-sm space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Sous-total:</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                
                {(discount || 0) > 0 && (
                  <div className="flex justify-between text-green-600">
                    <div className="flex flex-col">
                      <div className="flex items-center space-x-2">
                        <span>Réduction</span>
                        {editMode && user?.isAdmin ? (
                          <div className="flex items-center space-x-1">
                            <span>(</span>
                            <input
                              type="number"
                              value={discount || 0}
                              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                              className="w-16 text-center border border-gray-300 rounded px-1 py-0.5 text-xs"
                              min="0"
                              max="100"
                              step="0.1"
                            />
                            <span>%):</span>
                          </div>
                        ) : (
                          <span>({safeToFixed(discount)}%):</span>
                        )}
                      </div>
                      {location.state?.promoDiscount ? (
                        <span className="text-xs text-green-500">
                          Code: {location.state?.promoCode || 'Sélectionné'}
                        </span>
                      ) : userPromoCodes.length > 0 ? (
                        <span className="text-xs text-green-500">
                          Code promo automatique
                        </span>
                      ) : null}
                    </div>
                    <span>-{formatPrice(discountAmount)}</span>
                  </div>
                )}
                
                {editMode && user?.isAdmin && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {(discount || 0) === 0 ? 'Ajouter une réduction:' : 'Modifier la réduction:'}
                      </span>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          placeholder="0"
                          value={discount || 0}
                          onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                          className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                        <span className="text-sm text-gray-500">%</span>
                        {(discount || 0) > 0 && (
                          <button
                            onClick={() => setDiscount(0)}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                            title="Supprimer la réduction"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => addDiscount(5)}
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        5%
                      </button>
                      <button
                        onClick={() => addDiscount(10)}
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        10%
                      </button>
                      <button
                        onClick={() => addDiscount(15)}
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        15%
                      </button>
                      <button
                        onClick={() => addDiscount(20)}
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        20%
                      </button>
                      <button
                        onClick={() => addDiscount(50)}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        50%
                      </button>
                      <button
                        onClick={() => addDiscount(100)}
                        className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                      >
                        Offert
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between text-gray-600">
                  <span>TVA ({tva}%):</span>
                  <span>{formatPrice(tvaAmount)}</span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between text-xl font-bold text-gray-900">
                    <span>Total:</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Conditions et paiement */}
          <div className="bg-gray-50 px-8 py-6 border-t">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Conditions de paiement
                </h4>
                <div className="text-sm text-gray-600 space-y-2">
                  <p><strong>Paiement en 3 tranches:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>40% à la signature ({formatPrice(total * 0.4)})</li>
                    <li>40% à mi-parcours ({formatPrice(total * 0.4)})</li>
                    <li>20% à la livraison ({formatPrice(total * 0.2)})</li>
                  </ul>
                  <p className="mt-3"><strong>Modes de paiement acceptés:</strong></p>
                  <p>Virement bancaire, Mobile Money (Orange Money, Wave)</p>
                  
                  {hasNonFixedPrices && showPaymentNote && invoiceStatus === 'pending' && (
                    <div className="mt-4 p-3 bg-amber-100 border border-amber-300 rounded-md">
                      <div className="flex items-start justify-between">
                        <p className="text-xs text-amber-800 font-medium flex-1">
                          ⚠️ <strong>Note importante:</strong> Les montants ci-dessus sont provisoires pour les services "à partir de". 
                          Le paiement ne sera demandé qu'après validation de la facture définitive.
                        </p>
                        {user?.isAdmin && (
                          <button
                            onClick={() => setShowPaymentNote(false)}
                            className="text-amber-600 hover:text-amber-800 transition-colors ml-3 flex-shrink-0"
                            title="Masquer cette note"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Conditions générales
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Devis valable 30 jours</p>
                  <p>• Délai de livraison: 2-4 semaines selon le projet</p>
                  <p>• Support technique inclus pendant 3 mois</p>
                  <p>• Formation utilisateur incluse</p>
                  <p>• Modifications mineures incluses</p>
                </div>
              </div>
            </div>
            
            {/* Remerciement */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center space-x-2 text-blue-600">
                <Building2 className="w-5 h-5" />
                <span className="font-semibold">Merci de votre confiance !</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                L'équipe IRIS SOFTECH vous remercie pour votre projet. 
                Nous sommes impatients de travailler avec vous !
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modal pour ajouter des services/packs */}
      {showAddService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Ajouter un service ou pack</h2>
                <button
                  onClick={() => setShowAddService(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Trash2 className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {/* Services */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Services disponibles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableServices.map((service) => (
                    <div key={service.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-gray-900">{service.name}</h4>
                        <span className="text-sm font-semibold text-blue-600">
                          {service.price_type === 'from' ? 'À partir de ' : ''}
                          {formatPrice(service.price)}
                          {service.price_type === 'per_unit' ? '/page' : ''}
                        </span>
                      </div>
                      {service.description && (
                        <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                      )}
                      <button
                        onClick={() => addService(service)}
                        className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                      >
                        Ajouter ce service
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Packs */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Packs disponibles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availablePacks.map((pack) => (
                    <div key={pack.id} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-gray-900">{pack.name}</h4>
                        <span className="text-sm font-semibold text-purple-600">
                          {formatPrice(pack.total_price)}
                        </span>
                      </div>
                      {pack.description && (
                        <p className="text-sm text-gray-600 mb-3">{pack.description}</p>
                      )}
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-1">Services inclus:</p>
                        <div className="flex flex-wrap gap-1">
                          {pack.services?.slice(0, 3).map((service, index) => (
                            <span key={index} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              {service.name}
                            </span>
                          ))}
                          {pack.services && pack.services.length > 3 && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              +{pack.services.length - 3} autres
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => addPack(pack)}
                        className="w-full px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-sm"
                      >
                        Ajouter ce pack
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default FactureProformaPage;
